import React, { createContext, useContext } from "react";
import type { ReactNode } from "react";

interface HeaderContextType {
  title: string;
  subtitle: string;
  badge?: {
    text: string;
    className?: string;
  };
  actions?: ReactNode;
  setHeaderContent: (content: {
    title: string;
    subtitle: string;
    badge?: {
      text: string;
      className?: string;
    };
    actions?: ReactNode;
  }) => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

interface HeaderProviderProps {
  children: ReactNode;
  initialTitle?: string;
  initialSubtitle?: string;
  initialBadge?: {
    text: string;
    className?: string;
  };
  initialActions?: ReactNode;
}

export function HeaderProvider({
  children,
  initialTitle = "",
  initialSubtitle = "",
  initialBadge,
  initialActions,
}: HeaderProviderProps) {
  const [title, setTitle] = React.useState(initialTitle);
  const [subtitle, setSubtitle] = React.useState(initialSubtitle);
  const [badge, setBadge] = React.useState(initialBadge);
  const [actions, setActions] = React.useState(initialActions);

  const setHeaderContent = React.useCallback(
    ({
      title: newTitle,
      subtitle: newSubtitle,
      badge: newBadge,
      actions: newActions,
    }: {
      title: string;
      subtitle: string;
      badge?: {
        text: string;
        className?: string;
      };
      actions?: ReactNode;
    }) => {
      setTitle(newTitle);
      setSubtitle(newSubtitle);
      setBadge(newBadge);
      setActions(newActions);
    },
    [],
  );

  const value = React.useMemo<HeaderContextType>(() => {
    const base = { title, subtitle, actions, setHeaderContent };
    return badge ? { ...base, badge } : base;
  }, [title, subtitle, actions, setHeaderContent, badge]);

  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>;
}

export function useHeader(): HeaderContextType {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error("useHeader must be used within a HeaderProvider");
  }
  return context;
}
